import React, { useEffect, useRef, useState } from "react";

/**
 * StudioProjector — Dedicated Clean Output Display for OBS Window Capture.
 *
 * Renders at `/projector`. Contains zero UI chrome, zero toolbars, pure black background.
 * Attaches the live swapped MediaStream directly from `window.opener` with 0ms added latency.
 * Double-click enters/exits fullscreen mode.
 */
export function StudioProjector() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [hasStream, setHasStream] = useState(false);

  useEffect(() => {
    // Explicit title so OBS Window Capture displays it clearly in dropdowns
    document.title = "StreamPersona - Projector (Window Capture)";

    const attachStream = () => {
      try {
        const stream = (window.opener as any)?.__STREAM_PERSONA_OUTPUT_STREAM__ as MediaStream | undefined;
        if (stream && videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
          setHasStream(true);
          return true;
        }
      } catch (e) {
        console.warn("[Projector] Could not access opener stream:", e);
      }
      return false;
    };

    if (!attachStream()) {
      // Retry in case opener is still initializing
      const interval = setInterval(() => {
        if (attachStream()) {
          clearInterval(interval);
        }
      }, 500);

      const handleMessage = (e: MessageEvent) => {
        if (e.data?.type === "SP_STREAM_UPDATE") {
          attachStream();
        }
      };
      window.addEventListener("message", handleMessage);

      return () => {
        clearInterval(interval);
        window.removeEventListener("message", handleMessage);
      };
    }
  }, []);

  const handleDoubleClick = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };

  return (
    <div
      onDoubleClick={handleDoubleClick}
      style={{
        width: "100vw",
        height: "100vh",
        backgroundColor: "#000000",
        margin: 0,
        padding: 0,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "default",
        userSelect: "none",
      }}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: hasStream ? "block" : "none",
        }}
      />

      {!hasStream && (
        <div
          style={{
            color: "#6b7280",
            fontFamily: "system-ui, -apple-system, sans-serif",
            textAlign: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              border: "2px solid #2bb5a8",
              borderTopColor: "transparent",
              margin: "0 auto 16px",
              animation: "spin 1s linear infinite",
            }}
          />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          <h2 style={{ fontSize: "16px", fontWeight: 600, color: "#f3f4f6", margin: "0 0 6px" }}>
            StreamPersona Studio Projector
          </h2>
          <p style={{ fontSize: "13px", margin: 0, opacity: 0.8 }}>
            Waiting for live video feed from StreamPersona Studio...
          </p>
          <p style={{ fontSize: "11px", marginTop: "12px", color: "#4b5563" }}>
            Double-click window to toggle fullscreen. In OBS: Add Source → Window Capture → Select this window.
          </p>
        </div>
      )}
    </div>
  );
}
